import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowLeft, ArrowUp, ImagePlus, Save, Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getAdminProduct, saveProduct } from "../../lib/api";
import type { AspectRatio, Product, ProductImage } from "../../types/content";
import { AdminHeader, Field, Notice, Toggle } from "./AdminUi";

const emptyProduct: Product = {
  id: "",
  name: "",
  slug: "",
  shortDescription: "",
  longDescription: "",
  logoUrl: "",
  coverImageUrl: "",
  websiteUrl: "",
  playStoreUrl: "",
  appStoreUrl: "",
  platform: "",
  category: "",
  isFeatured: false,
  isActive: true,
  displayOrder: 1,
  images: [],
};

export function ProductEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["admin-product", id],
    queryFn: () => getAdminProduct(id!),
    enabled: Boolean(id),
  });
  const [product, setProduct] = useState<Product>(emptyProduct);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (query.data) setProduct(query.data);
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: () => saveProduct(product),
    onSuccess: (saved) => {
      setProduct(saved);
      setNotice("Product saved and public data refreshed.");
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      queryClient.invalidateQueries({ queryKey: ["public-landing"] });
      if (!id) navigate(`/admin/products/${saved.id}/edit`, { replace: true });
    },
  });

  function patch(values: Partial<Product>) {
    setProduct((current) => ({ ...current, ...values }));
  }

  function onNameChange(name: string) {
    patch({
      name,
      slug: product.id ? product.slug : name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
    });
  }

  function addImage() {
    const image: ProductImage = {
      id: `new-${crypto.randomUUID()}`,
      productId: product.id,
      imageUrl: "",
      altText: `${product.name || "Product"} screenshot ${product.images.length + 1}`,
      aspectRatio: "9:16",
      displayOrder: product.images.length + 1,
      isActive: true,
    };
    patch({ images: [...product.images, image] });
  }

  function updateImage(index: number, values: Partial<ProductImage>) {
    patch({ images: product.images.map((image, imageIndex) => imageIndex === index ? { ...image, ...values } : image) });
  }

  function removeImage(index: number) {
    patch({ images: product.images.filter((_, imageIndex) => imageIndex !== index).map((image, imageIndex) => ({ ...image, displayOrder: imageIndex + 1 })) });
  }

  function moveImage(index: number, direction: number) {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= product.images.length) return;
    const next = [...product.images];
    [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
    patch({ images: next.map((image, imageIndex) => ({ ...image, displayOrder: imageIndex + 1 })) });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <form onSubmit={submit}>
      <AdminHeader
        title={id ? `Edit ${product.name || "product"}` : "New product"}
        description="Product information, publishing controls, links, and an unlimited image gallery."
        action={
          <div className="admin-actions">
            <Link className="button button-secondary" to="/admin/products"><ArrowLeft size={16} /> Back</Link>
            <button className="button button-primary" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Saving..." : "Save product"} <Save size={16} />
            </button>
          </div>
        }
      />
      {notice && <Notice type="success" message={notice} onClose={() => setNotice("")} />}
      {mutation.isError && <Notice type="error" message="Could not save product. Check the required fields and image URLs." onClose={() => mutation.reset()} />}
      <div className="product-editor-grid">
        <div className="product-editor-main">
          <section className="admin-card editor-card">
            <div className="card-heading"><div><h2>Product information</h2><p>Core identity and descriptive content</p></div></div>
            <div className="form-grid">
              <Field label="Product name"><input required value={product.name} onChange={(event) => onNameChange(event.target.value)} /></Field>
              <Field label="Slug" hint="Used in the public product URL."><input required value={product.slug} onChange={(event) => patch({ slug: event.target.value })} /></Field>
              <Field label="Category"><input required value={product.category} onChange={(event) => patch({ category: event.target.value })} /></Field>
              <Field label="Platform"><input required value={product.platform} onChange={(event) => patch({ platform: event.target.value })} /></Field>
              <Field label="Short description"><textarea required rows={4} value={product.shortDescription} onChange={(event) => patch({ shortDescription: event.target.value })} /></Field>
              <Field label="Long description"><textarea required rows={7} value={product.longDescription} onChange={(event) => patch({ longDescription: event.target.value })} /></Field>
              <Field label="Logo URL"><input type="url" value={product.logoUrl} onChange={(event) => patch({ logoUrl: event.target.value })} /></Field>
              <Field label="Cover image URL"><input type="url" value={product.coverImageUrl} onChange={(event) => patch({ coverImageUrl: event.target.value })} /></Field>
              <Field label="Website URL"><input type="url" value={product.websiteUrl} onChange={(event) => patch({ websiteUrl: event.target.value })} /></Field>
              <Field label="Google Play URL"><input type="url" value={product.playStoreUrl} onChange={(event) => patch({ playStoreUrl: event.target.value })} /></Field>
              <Field label="Apple App Store URL"><input type="url" value={product.appStoreUrl} onChange={(event) => patch({ appStoreUrl: event.target.value })} /></Field>
            </div>
          </section>
          <section className="admin-card editor-card">
            <div className="card-heading">
              <div><h2>Image gallery</h2><p>Add as many images as needed. Each image preserves its own aspect ratio.</p></div>
              <button className="button button-secondary" type="button" onClick={addImage}>
                Add image <ImagePlus size={16} />
              </button>
            </div>
            <div className="gallery-editor">
              {product.images.map((image, index) => (
                <article key={image.id}>
                  <div className="gallery-preview" style={{ aspectRatio: image.aspectRatio === "custom" ? `${image.customWidth || 1}/${image.customHeight || 1}` : image.aspectRatio.replace(":", "/") }}>
                    {image.imageUrl ? <img src={image.imageUrl} alt={image.altText} /> : <span><ImagePlus size={25} /> Image preview</span>}
                  </div>
                  <div className="gallery-fields">
                    <div className="gallery-item-head">
                      <strong>Image {index + 1}</strong>
                      <div>
                        <button type="button" onClick={() => moveImage(index, -1)} disabled={index === 0}><ArrowUp size={15} /></button>
                        <button type="button" onClick={() => moveImage(index, 1)} disabled={index === product.images.length - 1}><ArrowDown size={15} /></button>
                        <button type="button" onClick={() => removeImage(index)}><Trash2 size={15} /></button>
                      </div>
                    </div>
                    <Field label="Image URL"><input type="url" required value={image.imageUrl} onChange={(event) => updateImage(index, { imageUrl: event.target.value })} /></Field>
                    <Field label="Alt text"><input required value={image.altText} onChange={(event) => updateImage(index, { altText: event.target.value })} /></Field>
                    <Field label="Aspect ratio">
                      <select value={image.aspectRatio} onChange={(event) => updateImage(index, { aspectRatio: event.target.value as AspectRatio })}>
                        <option value="1:1">1:1 — Square</option>
                        <option value="4:5">4:5 — Portrait</option>
                        <option value="9:16">9:16 — Mobile</option>
                        <option value="16:9">16:9 — Landscape</option>
                        <option value="custom">Custom</option>
                      </select>
                    </Field>
                    {image.aspectRatio === "custom" && (
                      <div className="custom-ratio">
                        <Field label="Width"><input type="number" min={1} value={image.customWidth || 1} onChange={(event) => updateImage(index, { customWidth: Number(event.target.value) })} /></Field>
                        <Field label="Height"><input type="number" min={1} value={image.customHeight || 1} onChange={(event) => updateImage(index, { customHeight: Number(event.target.value) })} /></Field>
                      </div>
                    )}
                  </div>
                </article>
              ))}
              {!product.images.length && <div className="gallery-editor-empty">Add an image URL to begin the product gallery.</div>}
            </div>
          </section>
        </div>
        <aside className="product-editor-side">
          <section className="admin-card publish-card">
            <div className="card-heading"><div><h2>Publishing</h2><p>Public visibility controls</p></div></div>
            <Toggle checked={product.isActive} onChange={(checked) => patch({ isActive: checked })} label="Active and public" />
            <Toggle checked={product.isFeatured} onChange={(checked) => patch({ isFeatured: checked })} label="Featured product" />
            <Field label="Display order"><input type="number" min={1} value={product.displayOrder} onChange={(event) => patch({ displayOrder: Number(event.target.value) })} /></Field>
          </section>
          <section className="admin-card editor-tip">
            <strong>Gallery guide</strong>
            <p>Use 9:16 for mobile screenshots, 16:9 for web previews, and “custom” only when the source image needs a unique ratio.</p>
          </section>
        </aside>
      </div>
    </form>
  );
}
